import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedBrokerageOrderListItem, SalesRegulatedApiService } from './sales-regulated-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-brokerage-orders',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, CheckboxModule, InputTextModule],
  template: `
    <ain-page [title]="t('sales.regulated.brokerage.title')" [description]="t('sales.regulated.brokerage.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.regulated.brokerage.accountsNav')" severity="secondary" (click)="goAccounts()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selected().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <div class="filters" [class.collapsed]="!filtersOpen()">
            <button type="button" class="filter-toggle" (click)="filtersOpen.set(!filtersOpen())">{{ t('sales.regulated.filters.title') }}</button>
            <div class="filter-body filter-grid">
              <label><span>{{ t('sales.regulated.brokerage.fields.symbol') }}</span><input pInputText [(ngModel)]="filterSymbol" (ngModelChange)="applyFilters()" /></label>
            </div>
          </div>
          <p-table [value]="filtered()" dataKey="id" [paginator]="true" [rows]="20" [selection]="selected()" (selectionChange)="onSelect($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.regulated.brokerage.fields.symbol') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.symbol }}</button></td>
                <td>{{ row.status }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesBrokerageOrdersComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly rows = signal<RegulatedBrokerageOrderListItem[]>([]);
  readonly filtered = signal<RegulatedBrokerageOrderListItem[]>([]);
  readonly selected = signal<RegulatedBrokerageOrderListItem[]>([]);
  readonly filtersOpen = signal(true);
  filterSymbol = '';
  ngOnInit(): void { this.load(); }
  load(): void {
    this.loading.set(true);
    this.api.listBrokerageOrders().subscribe({
      next: r => { this.rows.set(r); this.applyFilters(); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
  }
  applyFilters(): void {
    const s = this.filterSymbol.trim().toUpperCase();
    this.filtered.set(this.rows().filter(r => !s || r.symbol.toUpperCase().includes(s)));
  }
  onSelect(v: RegulatedBrokerageOrderListItem[]): void { this.selected.set(v ?? []); }
  openCreate(): void { void this.router.navigate(['/sales/brokerage-orders/new']); }
  openSelected(): void { const id = this.selected()[0]?.id; if (id) this.openDetail(id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/brokerage-orders', id]); }
  goAccounts(): void { void this.router.navigate(['/sales/brokerage-accounts']); }
  t(key: string): string { return this.localization.translate(key); }
}
